import { Stack } from '@mui/material';
import React, { ReactNode, useCallback, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { isEmpty } from 'lodash';
import { selectCard } from 'js/actions';
import TagsView from 'js/components/elements/tags/TagsView';
import { SchemaDetailHeader } from 'js/components/schemaSpecs/cards/SchemaDetailHeader';
import SchemaSpecDetailActionsContainer from 'js/components/schemaSpecs/cards/SchemaSpecDetailActionsContainer';
import { SearchFilterViewsType } from 'js/constants/visibilityFilters';
import { isOwner, ownershipLabel, shouldDisplayOwnershipLabel } from 'js/lib/ACLUtils';
import DataCardList from 'new/elements/DataCardList/DataCardList';
import { useListOldReduxContainer } from 'new/elements/DataCard/useListOldReduxContainer';
import { mapStateToProps } from 'js/components/data_sets/cards/stateMapper';
import DateUtils from 'js/lib/DateUtils';
import { ChipWithCopy } from 'new/elements/ChipWithCopy';
import DataCard, { DataCardCell, NameDescCell } from 'new/elements/DataCard';
import Typography from 'new/elements/TypographySupport/TypographySupport';
import { AvailableFilters } from '../MacroInput/types/AvailableFilters';
import { DetailsIconButton } from 'new/elements/Button';

import s from './SchemaSpecsTable.module.scss';

type Props = {
  urlId?: string;
  data: any[];
  isLoading: boolean;
  availableFilterTypes: string[];
  view: SearchFilterViewsType;
  detailViewTabs: any[];
  loadData: (page: number, sortCriteria: string, availableFilters: AvailableFilters) => void;
  onEditButtonClicked: (tx: string) => void;
  noContentView?: ReactNode;
};

const makeRowRenderer =
  ({ onEditButtonClicked, handleCardSelected, getData }) =>
  (index, item) => {
    const { name, description, createdAt, cardData, readOnly, aclQueryMode, owner, isManaged } =
      getData(index);
    const readOnlyLabel = cardData.public
      ? 'PUBLIC'
      : shouldDisplayOwnershipLabel(aclQueryMode)
        ? ownershipLabel(cardData)
        : null;

    const onClickCard = () => handleCardSelected(cardData);

    return (
      <DataCard
        key={item.id || index}
        onClick={onClickCard}
        rowsSlots={[
          <>
            <NameDescCell name={name} description={description} />
            {readOnlyLabel && (
              <DataCardCell className={s.dataCardCellPublicLabelContainer}>
                <Typography variant="caption">{readOnlyLabel}</Typography>
                {!isOwner(cardData) && (
                  <Typography variant="body3">
                    <b>OWNED BY: </b> {owner.email}
                  </Typography>
                )}
              </DataCardCell>
            )}
            <DataCardCell rightCTAsCell>
              <DetailsIconButton onClick={onClickCard} />
              {!cardData.public && (
                <SchemaSpecDetailActionsContainer
                  data={cardData}
                  openDetailsModal={onClickCard}
                  onEditButtonClicked={onEditButtonClicked}
                  disableEditing={false}
                  disableEditMessage={null}
                  readOnly={readOnly}
                  isManaged={isManaged}
                />
              )}
            </DataCardCell>
          </>,
          !isEmpty(cardData.tags) && (
            <DataCardCell>
              <TagsView data={cardData} />
            </DataCardCell>
          ),
          <DataCardCell multiCol>
            <Stack spacing="8px" direction="row" alignItems="flex-end">
              <ChipWithCopy tooltipTitle="Copy Schema Template ID" toCopy={cardData.id}>
                ID {cardData.id}
              </ChipWithCopy>
              <Typography variant="body3">
                <b>CREATED ON:</b> {createdAt}
              </Typography>
            </Stack>
          </DataCardCell>,
        ].filter(Boolean)}
      />
    );
  };

const SchemaSpecsTable = ({
  onEditButtonClicked,
  availableFilterTypes,
  view,
  urlId,
  isLoading,
  data,
  detailViewTabs,
  loadData,
  noContentView,
}: Props) => {
  const dispatch = useDispatch();
  const listing = useSelector((state: any) => state.app.listing);
  const { selectedCardId, aclQueryMode } = listing;
  const handleCardSelected = useCallback((row) => {
    dispatch(selectCard(row.id));
  }, []);
  useEffect(() => {
    if (urlId) {
      handleCardSelected({ id: urlId });
    }
  }, []);

  const getData = useListOldReduxContainer(
    data,
    mapStateToProps,
    (ownProps) => {
      const { cardData, readOnly, adminAccess, isManaged, owner } = ownProps;

      if (!cardData) {
        return null;
      }

      const name = cardData.name || '';
      const description = cardData.description || ' ';

      const createdAt = DateUtils.convertDateStringToFormat(cardData.created_at);

      return {
        name,
        description,
        createdAt,
        cardData,
        readOnly,
        adminAccess,
        aclQueryMode,
        owner,
        isManaged,
      };
    },
    [aclQueryMode],
  );

  return (
    <DataCardList
      data={data}
      view={view}
      availableFilterTypes={availableFilterTypes}
      detailViewTabs={detailViewTabs}
      urlId={urlId}
      loading={isLoading}
      detailHeaderContent={
        <SchemaDetailHeader
          {...getData(data.findIndex((item: any) => item?.id === selectedCardId))}
          onEditButtonClicked={onEditButtonClicked}
        />
      }
      rowRenderer={makeRowRenderer({
        onEditButtonClicked,
        handleCardSelected,
        getData,
      })}
      noContentView={noContentView}
      loadData={loadData}
    />
  );
};

export default SchemaSpecsTable;
